import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/core/auth/hooks/useAuth";
import { profileApiService } from "../services/profileApiService";
import {
  type CreateProfileRequest,
  type Profile,
  type UpdateProfileRequest,
  fromCreateProfileRequestToCreateProfileDto,
  fromProfileDtoToProfile,
  fromUpdateProfileRequestToUpdateProfileDto,
} from "../types/profileTypes";

const staleTime = 30 * 60 * 1000; // 30 minutes

// Query keys
export const profileKeys = {
  all: ["profile"] as const,
  me: (userId?: string) => [...profileKeys.all, "me", userId] as const,
};

export const useProfile = () => {
  const { user, loading, getAuthGeneration } = useAuth();
  return useQuery<Profile | null, Error>({
    queryKey: profileKeys.me(user?.id),
    queryFn: async ({ signal }) => {
      const generation = getAuthGeneration();
      try {
        const dto = await profileApiService.getMe();
        signal.throwIfAborted();
        if (generation !== getAuthGeneration()) throw new Error("Your account changed.");
        const profile = fromProfileDtoToProfile(dto);
        if (profile.userId !== user?.id) throw new Error("The profile belongs to a different account.");
        return profile;
      } catch (error) {
        signal.throwIfAborted();
        if (generation !== getAuthGeneration()) throw new Error("Your account changed.");
        if (
          error instanceof Error &&
          error.message.includes("Profile not found")
        ) {
          return null;
        }
        throw error;
      }
    },
    staleTime: staleTime,
    enabled: !!user && !loading,
  });
};

interface ProfileMutationScope {
  userId: string;
  generation: number;
}

function useProfileCache() {
  const queryClient = useQueryClient();
  const { user, getAuthGeneration } = useAuth();
  return {
    onMutate: async (): Promise<ProfileMutationScope> => {
      if (!user) throw new Error("Sign in before changing your profile.");
      const scope = { userId: user.id, generation: getAuthGeneration() };
      await queryClient.cancelQueries({ queryKey: profileKeys.me(scope.userId) });
      if (scope.generation !== getAuthGeneration()) throw new Error("Your account changed.");
      return scope;
    },
    onSuccess: async (profile: Profile | null, _request: unknown, scope: ProfileMutationScope | undefined) => {
      if (!scope || scope.generation !== getAuthGeneration()) return;
      if (profile && profile.userId !== scope.userId) throw new Error("Profile not found in this account.");
      await queryClient.cancelQueries({ queryKey: profileKeys.me(scope.userId) });
      if (scope.generation !== getAuthGeneration()) return;
      queryClient.setQueryData(profileKeys.me(scope.userId), profile);
    },
  };
}

export const useCreateProfile = () => {
  const cache = useProfileCache();
  return useMutation<Profile, Error, CreateProfileRequest, ProfileMutationScope>({
    mutationFn: async (data: CreateProfileRequest) => {
      const dto = fromCreateProfileRequestToCreateProfileDto(data);
      const resultDto = await profileApiService.create(dto);
      return fromProfileDtoToProfile(resultDto);
    },
    ...cache,
  });
};

export const useUpdateProfile = () => {
  const cache = useProfileCache();

  return useMutation<Profile, Error, UpdateProfileRequest, ProfileMutationScope>({
    mutationFn: async (data: UpdateProfileRequest) => {
      const dto = fromUpdateProfileRequestToUpdateProfileDto(
        data as UpdateProfileRequest
      );
      const resultDto = await profileApiService.update(dto);
      return fromProfileDtoToProfile(resultDto);
    },
    ...cache,
  });
};

export const useDeleteProfile = () => {
  const cache = useProfileCache();

  return useMutation<{ message: string }, Error, void, ProfileMutationScope>({
    mutationFn: () => profileApiService.delete(),
    onMutate: cache.onMutate,
    onSuccess: (_result, request, scope) => cache.onSuccess(null, request, scope),
  });
};
